import React, { CSSProperties, FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, isMobileDevice, Responsive } from 'rs-emd-ui-atoms';
import { IHeroBanner } from '../../../../models/campaign-pages/widgets/hero-banner/hero-banner.model';
import ImageWidget from '../image/image-widget-component';
import TextWidget from '../text-widget/text-widget-component';
import styles from './hero-banner-component.module.scss';

export type HeroBannerProps = {
  banner: IHeroBanner;
  isInCarousel?: boolean;
  numOfBannersInCarousel?: number;
  currentCarouselIndex?: number;
  setCarouselIndex?: (index: number) => void;
};

export const HeroBanner: FunctionComponent<HeroBannerProps> = (props: HeroBannerProps) => {
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const [contentContainerWidth, setContentContainerWidth] = useState<number>();

  let ContainerTag = (props.banner.link?.URL ? 'a' : 'div') as keyof JSX.IntrinsicElements;
  let linkAttributes =
    ContainerTag === 'a' ? { href: props.banner.link?.URL ?? undefined, target: props.banner.link?.target ?? undefined } : {};

  //texts
  let texts = props.banner.text && (
    <div className={styles.text}>
      <TextWidget
        textWidget={{
          textData: props.banner.text.map((txt) => {
            return { ...txt, textSize: txt.textSize ?? 'md' };
          }),
        }}
        isContainerFullHeight={false}></TextWidget>
    </div>
  );

  //button
  let button = (props.banner.button?.text || props.banner.button?.icon) && (
    <div
      className={`${styles.button} ${styles['button-horiz-align-' + props.banner.buttonHorizontalAlignment?.desktop]} ${
        styles['mobile-button-horiz-align-' + props.banner.buttonHorizontalAlignment?.mobile]
      } `}>
      <Button
        {...linkAttributes}
        text={props.banner.button.type !== 'buttonWithIcon' && props.banner.button.text ? props.banner.button.text : undefined}
        icon={props.banner.button.type !== 'buttonWithText' && props.banner.button.icon ? props.banner.button.icon : undefined}
        buttonType={props.banner.button?.style ?? undefined}
        isExternalLink={true}
        size={props.banner.button.type === 'buttonWithIcon' ? 'small-icon' : 'default'}
        iconPosition={props.banner.button.type === 'buttonWithTextAndIconRight' ? 'right' : 'left'}
      />
    </div>
  );

  function getColour(colour: string | null | undefined) {
    return colour?.replace('#', '');
  }

  //texts and button
  function content() {
    let cssClass, textAlignment, buttonAlignment;

    // this is aleady being re-rendered on every resize due to the change in contentWidth
    if (isMobileDevice()) {
      textAlignment = props.banner.textAlignment?.mobile;
      buttonAlignment = props.banner.buttonVerticalAlignment?.mobile;

      if (props.banner.mobileContentStyle === 'below') {
        cssClass = `${styles['overlay-color-' + getColour(props.banner.mobileOverlayColour)]}`;
      }
    } else {
      textAlignment = props.banner.textAlignment?.desktop;
      buttonAlignment = props.banner.buttonVerticalAlignment?.desktop;

      cssClass = `${styles['overlay-style-' + props.banner.desktopContentStyle]}`;

      if (props.banner.desktopContentStyle === 'slash') {
        cssClass = `${cssClass}
       ${styles['slash-' + props.banner.desktopContentPosition]}
       ${styles['overlay-color-' + getColour(props.banner.slashOverlayColour)]}`;
      } else if (props.banner.desktopContentStyle === 'curve') {
        cssClass = `${cssClass} 
        ${styles['curve-' + props.banner.desktopContentPosition]}
        ${styles['overlay-color-' + getColour(props.banner.curveOverlayColour)]}`;
      } else {
        cssClass = `${cssClass} 
       ${styles['overlay-color-' + getColour(props.banner.blockOverlayColour)]}`;
      }
    }

    return (
      <div
        ref={contentContainerRef}
        className={`${styles['content-container']} ${cssClass} 
            ${
              props.banner.button?.text || props.banner.button?.icon
                ? props.banner.text
                  ? styles['content-vert-align-text-' + textAlignment + '-button-' + buttonAlignment]
                  : styles['button-vert-align-' + buttonAlignment]
                : styles['text-vert-align-' + textAlignment]
            } `}>
        {texts}
        {button}
      </div>
    );
  }

  // set content width on resize
  useLayoutEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };

    function onResize() {
      setContentContainerWidth(contentContainerRef.current?.clientWidth);
    }
  }, [props]);

  useEffect(() => {
    setContentContainerWidth(contentContainerRef.current?.clientWidth);
  }, [contentContainerRef]);

  return (
    <ContainerTag
      {...linkAttributes}
      className={`${styles['hero-banner-container']} ${
        props.banner.mobileContentStyle === 'below' ? styles['mobile-text-under-image'] : styles['mobile-text-on-image']
      } `}
      style={
        {
          '--content-width': `${contentContainerWidth + 'px'}`,
          '--md-horizontal-alignment':
            props.banner.desktopContentPosition === 'left'
              ? 'start'
              : props.banner.desktopContentPosition === 'right'
              ? 'flex-end'
              : props.banner.desktopContentPosition,
        } as CSSProperties
      }>
      <div className={styles['content-wrapper']} style={{ width: `${props.banner.desktopContentWidth ?? '33%'}` }}>
        <Responsive breakpoints={['tablet', 'desktop', 'lg-desktop', 'xl-desktop']}>
          {props.banner.desktopContentStyle === 'slash' &&
          (props.banner.desktopContentPosition === 'left' || props.banner.desktopContentPosition === 'right') ? (
            <div
              className={`${styles['slash-container']} ${
                styles[props.banner.desktopContentPosition === 'left' ? 'slash-left' : 'slash-right']
              }`}>
              {/**Right slash triangles */}
              {props.banner.desktopContentPosition === 'right' && (
                <div className={`${styles['slash-triangles']}`}>
                  <svg viewBox='0 0 180 406' fill='none' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>
                    <path d='M0 0H180V405.125L0 0Z' className={styles[`${getColour(props.banner.slashOverlayColour)}`]} />
                  </svg>
                </div>
              )}
              {content()}
              {/**Left slash triangles */}
              {props.banner.desktopContentPosition === 'left' && (
                <div className={`${styles['slash-triangles']}`}>
                  <svg viewBox='0 0 180 405' fill='none' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>
                    <path d='M180 0H0V405L180 0Z' className={styles[`${getColour(props.banner.slashOverlayColour)}`]} />
                  </svg>
                </div>
              )}
            </div>
          ) : (
            content()
          )}
        </Responsive>

        <Responsive breakpoints={['mobile']}>
          {/* Carousel dots */}
          {props.isInCarousel &&
            props.numOfBannersInCarousel !== undefined &&
            props.currentCarouselIndex !== undefined &&
            props.setCarouselIndex !== undefined && (
              <div className={styles.dots}>
                {Array.from({ length: props.numOfBannersInCarousel }, (item, index) => index).map((k, index) => {
                  return (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        props.setCarouselIndex !== undefined && props.setCarouselIndex(index);
                      }}
                      className={`${styles.dot} ${props.currentCarouselIndex === index ? styles['dot-selected'] : ''}`}></div>
                  );
                })}
              </div>
            )}

          {/* Mobile slash */}
          {props.banner.mobileContentStyle === 'below' && (
            <div className={styles['slash-triangles']}>
              <svg viewBox='0 0 320 40' fill='none' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>
                <path d='M0 40H320V0L0 40Z' className={styles[`${getColour(props.banner.mobileOverlayColour)}`]} />
              </svg>
            </div>
          )}

          {content()}
        </Responsive>
      </div>

      {/* Image */}
      <ImageWidget image={{ image: props.banner.image }} imageWrapperClassName={styles.image} />
    </ContainerTag>
  );
};

export default HeroBanner;
